import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { getProduct, updateProduct, createVariant, updateVariant, addRate, setRateStatus, listVendors, catalogueMeta, listProductImages, uploadProductImage, setPrimaryImage, deleteProductImage, getListing, saveListing, setListingStatus, setPrice, removePrice } from "../../services/catalogueService";
import { listOrganizations } from "../../services/orgService";
import { errorToast, successToast } from "../Toasts/Toasts";

// CMS: one product from Zoho CRM - its variants (sizes), each vendor's
// current rate per variant, the full rate history, and the writes the
// rules allow: edit the product, add a variant, add a rate, change a
// rate's status.

const inr = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Almost every picture we hold came off a vendor's WhatsApp message with a
// price label on it. The original is kept as evidence of the quote; the
// cleaned version is what a gifting listing shows, and the primary is what
// any listing picks up.
// Whether a society may see this plant at all, under what name, and at what
// price. Nothing in Zoho reaches a customer without a row here at Approved:
// the vendor side is full of local shorthand and plants nobody would sell.
const CustomerListing = ({ productId, productName, variants }) => {
  const [listing, setListing] = useState(null);
  const [prices, setPrices] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState(null);
  const [priceForm, setPriceForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getListing(productId);
      setListing(r.listing);
      setPrices(r.prices || []);
      const base = { displayName: productName || "", description: "", careNotes: "", category: "", subCategory: "", unit: "each", coverage: "", coverageBasis: "", minOrder: "" };
      setForm(r.listing
        ? { ...base, displayName: r.listing.displayName, description: r.listing.description || "", careNotes: r.listing.careNotes || "",
            category: r.listing.category || "", subCategory: r.listing.subCategory || "", unit: r.listing.unit || "each",
            coverage: r.listing.coverage ?? "", coverageBasis: r.listing.coverageBasis || "", minOrder: r.listing.minOrder ?? "" }
        : base);
    } catch (e) { errorToast(e?.message); }
  }, [productId, productName]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { listOrganizations().then((r) => setOrgs(r.rows || r || [])).catch(() => setOrgs([])); }, []);
  const [meta, setMeta] = useState({ listingCategories: [], listingSubCategories: {}, units: [], coverageBases: [] });
  useEffect(() => { catalogueMeta().then(setMeta).catch(() => {}); }, []);

  const save = async () => {
    setBusy(true);
    try { const r = await saveListing(productId, form); if (r?.success) { successToast("Saved"); load(); } else errorToast(r?.message); }
    catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const status = async (next) => {
    setBusy(true);
    try { const r = await setListingStatus(productId, next); if (r?.success) { successToast(next === "Approved" ? "Customers can see this now" : `Marked ${next}`); load(); } else errorToast(r?.message); }
    catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const savePrice = async () => {
    setBusy(true);
    try {
      const r = await setPrice(productId, { ...priceForm, organizationId: priceForm.organizationId || null });
      if (r?.success) { setPriceForm(null); load(); } else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const dropPrice = async (id) => {
    try { const r = await removePrice(id); if (r?.success) load(); else errorToast(r?.message); } catch (e) { errorToast(e?.message); }
  };

  if (!form) return null;
  const orgName = (id) => (orgs.find((o) => o.id === id) || {}).name || `org ${id}`;
  const standard = prices.filter((p) => p.organizationId === null);
  const overrides = prices.filter((p) => p.organizationId !== null);
  const approved = listing && listing.status === "Approved";

  return (
    <div className="card mb-2">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="fw-bold mb-0">
              Customer catalogue
              {listing && <span className={`badge ms-2 bg-${approved ? "success" : listing.status === "Withdrawn" ? "secondary" : "warning text-dark"}`}>{listing.status}</span>}
              {!listing && <span className="badge bg-light text-dark ms-2">not listed</span>}
            </h6>
            <div className="small text-muted">Societies see only approved plants, and only the name, size and price below.</div>
          </div>
          <div className="d-flex gap-2">
            {!approved && <Button size="sm" variant="success" disabled={busy || !listing} onClick={() => status("Approved")}>Approve</Button>}
            {approved && <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => status("Withdrawn")}>Withdraw</Button>}
          </div>
        </div>

        <div className="row g-2">
          <div className="col-md-4"><Form.Label className="small mb-0">Name customers see</Form.Label><Form.Control size="sm" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></div>
          <div className="col-md-3">
            <Form.Label className="small mb-0">Category</Form.Label>
            <Form.Select size="sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, subCategory: "" })}>
              <option value="">— choose —</option>
              {(meta.listingCategories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </div>
          <div className="col-md-3">
            <Form.Label className="small mb-0">Shelf — what it is for</Form.Label>
            <Form.Select size="sm" value={form.subCategory} onChange={(e) => setForm({ ...form, subCategory: e.target.value })} disabled={!form.category}>
              <option value="">— choose —</option>
              {((meta.listingSubCategories || {})[form.category] || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </div>
          <div className="col-md-5"><Form.Label className="small mb-0">Description</Form.Label><Form.Control size="sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="col-12">
            <div className="row g-2 align-items-end border-top pt-2 mt-1">
              <div className="col-12"><div className="small fw-semibold">How much does a customer ask for?</div>
                <div className="small text-muted">A manager can measure a lawn or pace a boundary. Set a conversion and the portal asks for that instead of a number they have to guess.</div></div>
              <div className="col-md-2">
                <Form.Label className="small mb-0">Sold by</Form.Label>
                <Form.Select size="sm" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                  {(meta.units || ["each"]).map((u) => <option key={u} value={u}>{u}</option>)}
                </Form.Select>
              </div>
              <div className="col-md-3">
                <Form.Label className="small mb-0">They measure</Form.Label>
                <Form.Select size="sm" value={form.coverageBasis} onChange={(e) => setForm({ ...form, coverageBasis: e.target.value })}>
                  <option value="">nothing — they enter a number</option>
                  {(meta.coverageBases || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </div>
              <div className="col-md-3">
                <Form.Label className="small mb-0">{form.unit === "each" ? "Plants" : form.unit} per {form.coverageBasis || "unit"}</Form.Label>
                <Form.Control size="sm" type="number" step="0.01" min="0" value={form.coverage} onChange={(e) => setForm({ ...form, coverage: e.target.value })} disabled={!form.coverageBasis} placeholder={form.coverageBasis ? "e.g. 4" : "—"} />
              </div>
              <div className="col-md-2">
                <Form.Label className="small mb-0">Minimum order</Form.Label>
                <Form.Control size="sm" type="number" min="0" value={form.minOrder} onChange={(e) => setForm({ ...form, minOrder: e.target.value })} placeholder="optional" />
              </div>
            </div>
          </div>
          <div className="col-md-10"><Form.Label className="small mb-0">Care notes</Form.Label><Form.Control size="sm" value={form.careNotes} onChange={(e) => setForm({ ...form, careNotes: e.target.value })} /></div>
          <div className="col-md-2 d-flex align-items-end"><Button size="sm" variant="outline-success" className="w-100" disabled={busy || !form.displayName.trim()} onClick={save}>Save</Button></div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3 mb-1">
          <div className="fw-semibold small">Prices</div>
          <Button size="sm" variant="outline-primary" onClick={() => setPriceForm({ zohoVariantId: variants[0] ? variants[0].id : "", variantName: variants[0] ? variants[0].name : "", organizationId: "", price: "" })} disabled={!variants.length}>Set a price</Button>
        </div>
        {standard.length || overrides.length ? (
          <table className="table table-sm mb-0">
            <thead><tr><th>Size</th><th>Applies to</th><th className="text-end">Price</th><th>Note</th><th></th></tr></thead>
            <tbody>
              {[...standard, ...overrides].map((p) => (
                <tr key={p.id}>
                  <td>{p.variantName || p.zohoVariantId}</td>
                  <td className="small">{p.organizationId === null ? <span className="text-muted">every customer</span> : orgName(p.organizationId)}</td>
                  <td className="text-end">{inr(p.price)}</td>
                  <td className="small text-muted">{p.notes || ""}</td>
                  <td className="text-end"><Button size="sm" variant="link" className="p-0 text-danger" onClick={() => dropPrice(p.id)}>remove</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="small text-muted">No price yet — a plant cannot be approved until at least one size has a standard price.</div>}
      </div>

      <Modal show={!!priceForm} onHide={() => setPriceForm(null)}>
        <Modal.Header closeButton><Modal.Title>Set a price</Modal.Title></Modal.Header>
        {priceForm && <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Size</Form.Label>
            <Form.Select value={priceForm.zohoVariantId} onChange={(e) => setPriceForm({ ...priceForm, zohoVariantId: e.target.value, variantName: (variants.find((v) => v.id === e.target.value) || {}).name || "" })}>
              {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Applies to</Form.Label>
            <Form.Select value={priceForm.organizationId} onChange={(e) => setPriceForm({ ...priceForm, organizationId: e.target.value })}>
              <option value="">Every customer (standard price)</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>Only {o.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group><Form.Label>Price per plant (₹, incl. nothing else)</Form.Label><Form.Control type="number" min="1" step="0.01" value={priceForm.price} onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })} autoFocus /></Form.Group>
          <Form.Group className="mt-2"><Form.Label>Note</Form.Label><Form.Control value={priceForm.notes || ""} onChange={(e) => setPriceForm({ ...priceForm, notes: e.target.value })} placeholder="AMC rate, bulk agreement…" /></Form.Group>
        </Modal.Body>}
        <Modal.Footer><Button variant="success" disabled={busy || !priceForm?.price} onClick={savePrice}>Save price</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

const Images = ({ productId, productName }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setRows(await listProductImages(productId)); } catch (e) { /* a product with no pictures is normal */ }
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (event, kind) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      form.append("productName", productName || "");
      const r = await uploadProductImage(productId, form);
      if (r?.success) load(); else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const makePrimary = async (id) => {
    try { const r = await setPrimaryImage(id); if (r?.success) load(); else errorToast(r?.message); } catch (e) { errorToast(e?.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this picture from the product? The file itself is kept.")) return;
    try { await deleteProductImage(id); load(); } catch (e) { errorToast(e?.message); }
  };

  return (
    <div className="card mb-2">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h6 className="fw-bold mb-0">Pictures</h6>
            <div className="small text-muted">The primary one is what a listing uses. "Cleaned" means the price label and background have been taken off.</div>
          </div>
          <div className="d-flex gap-2">
            <label className={`btn btn-sm btn-outline-secondary mb-0 ${busy ? "disabled" : ""}`}>
              Add original<input type="file" accept="image/*" hidden onChange={(e) => upload(e, "original")} />
            </label>
            <label className={`btn btn-sm btn-outline-success mb-0 ${busy ? "disabled" : ""}`}>
              Add cleaned<input type="file" accept="image/*" hidden onChange={(e) => upload(e, "cleaned")} />
            </label>
          </div>
        </div>
        {rows.length ? (
          <div className="d-flex flex-wrap gap-3">
            {rows.map((img) => (
              <div key={img.id} style={{ width: 150 }}>
                <a href={img.url} target="_blank" rel="noreferrer">
                  <img src={img.url} alt={img.zohoProductName || "plant"} style={{ width: 150, height: 150, objectFit: "contain", background: "#fff", border: img.isPrimary ? "2px solid #198754" : "1px solid #dee2e6", borderRadius: 4 }} />
                </a>
                <div className="small mt-1">
                  <span className={`badge bg-${img.kind === "cleaned" ? "success" : "light text-dark"}`}>{img.kind}</span>
                  {img.isPrimary && <span className="badge bg-dark ms-1">primary</span>}
                </div>
                <div className="small">
                  {!img.isPrimary && <Button size="sm" variant="link" className="p-0 me-2" onClick={() => makePrimary(img.id)}>make primary</Button>}
                  <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => remove(img.id)}>remove</Button>
                </div>
                {img.sourceIntakeId && <div className="small text-muted">from WhatsApp #{img.sourceIntakeId}</div>}
              </div>
            ))}
          </div>
        ) : (
          <div className="small text-muted">No pictures yet. A vendor's photo can be attached here, or it arrives on its own when a rate is confirmed from a photo.</div>
        )}
      </div>
    </div>
  );
};
const ageDays = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);
const STATUS_CLASS = { Active: "success", Invalid: "danger", Superseded: "secondary", Disputed: "warning text-dark" };

const ProductPage = () => {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [meta, setMeta] = useState({ categories: [], nameSources: [], rateStatuses: [] });
  const [edit, setEdit] = useState(null); // product edit form
  const [variantModal, setVariantModal] = useState(null); // { id? , height, bagSize }
  const [rateModal, setRateModal] = useState(null); // { variantId, variantName, vendorId, rate, effectiveDate, confidence, remarks }
  const [open, setOpen] = useState({}); // variantId -> history shown
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setP(await getProduct(id)); } catch (e) { errorToast(e?.message || "Could not load"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { listVendors().then(setVendors).catch(() => {}); catalogueMeta().then(setMeta).catch(() => {}); }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (r?.success) { if (okMsg) successToast(okMsg); return r; } errorToast(r?.message); }
    catch (e) { errorToast(e?.message || "Failed"); }
    finally { setBusy(false); }
    return null;
  };

  const saveProduct = async () => {
    const r = await run(() => updateProduct(id, edit), "Saved");
    if (r) { setEdit(null); load(); }
  };
  const saveVariant = async () => {
    const r = variantModal.id
      ? await run(() => updateVariant(variantModal.id, { height: variantModal.height, bagSize: variantModal.bagSize }), "Variant updated")
      : await run(() => createVariant(id, { height: variantModal.height, bagSize: variantModal.bagSize }), "Variant created in Zoho");
    if (r) { setVariantModal(null); load(); }
  };
  const saveRate = async () => {
    const r = await run(() => addRate(rateModal.variantId, { vendorId: rateModal.vendorId, rate: Number(rateModal.rate), effectiveDate: rateModal.effectiveDate || undefined, confidence: rateModal.confidence, remarks: rateModal.remarks || undefined }), "Rate logged in Zoho");
    if (r) { setRateModal(null); load(); }
  };
  const changeStatus = async (rate, status) => {
    if (!window.confirm(`Mark "${rate.name}" as ${status}?`)) return;
    const r = await run(() => setRateStatus(rate.id, status), `Marked ${status}`);
    if (r) load();
  };

  if (!p) return null;
  const realVendors = vendors.filter((v) => !v.unidentified);

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 mb-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <div className="small"><Link to="/catalogue/products">Products</Link> / {p.name}</div>
            <h6 className="fw-bold mb-0">{p.name} {p.category && <span className="badge bg-light text-dark">{p.category}</span>}</h6>
            <div className="small text-muted"><i>{p.scientificName || "botanical name not set"}</i>{p.nameSource ? ` · ${p.nameSource}` : ""}</div>
          </div>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => setEdit({ name: p.name, scientificName: p.scientificName || "", category: p.category || "", nameSource: p.nameSource || "" })}>Edit product</Button>
            <Button size="sm" variant="outline-success" onClick={() => setVariantModal({ height: "", bagSize: "" })}>Add variant</Button>
          </div>
        </div>

        <Images productId={p.id} productName={p.name} />

        <CustomerListing productId={p.id} productName={p.name} variants={p.variants.map((v) => ({ id: v.id, name: v.name }))} />

        {p.variants.map((v) => (
          <div className="card mb-2" key={v.id}>
            <div className="card-body py-2">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <span className="fw-semibold">{v.name}</span>
                  <span className="small text-muted ms-2">Height {v.height || "—"} · Bag {v.bagSize || "—"}</span>
                </div>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => setVariantModal({ id: v.id, height: v.height && v.height !== "Unspecified" ? v.height : "", bagSize: v.bagSize && v.bagSize !== "Unspecified" ? v.bagSize : "" })}>Edit size</Button>
                  <Button size="sm" variant="success" onClick={() => setRateModal({ variantId: v.id, variantName: v.name, vendorId: "", rate: "", effectiveDate: "", confidence: "High", remarks: "" })}>Add rate</Button>
                </div>
              </div>
              {v.current.length ? (
                <table className="table table-sm mb-1 mt-2">
                  <thead><tr><th>Vendor</th><th className="text-end">Current rate</th><th>Quoted on</th><th>Source</th><th></th></tr></thead>
                  <tbody>
                    {v.current.map((r) => {
                      const age = ageDays(r.effectiveDate);
                      return (
                        <tr key={r.id}>
                          <td><Link to={`/catalogue/vendors/${r.vendorId}`}>{r.vendorName}</Link></td>
                          <td className="text-end fw-semibold">{inr(r.rate)}</td>
                          <td>{r.effectiveDate}{age > 60 ? <span className="badge bg-warning text-dark ms-1">{age} days old</span> : null}</td>
                          <td className="small text-muted">{r.source || ""}{r.confidence ? ` · ${r.confidence}` : ""}</td>
                          <td className="text-end">
                            <Button size="sm" variant="link" className="p-0 me-2" onClick={() => setRateModal({ variantId: v.id, variantName: v.name, vendorId: r.vendorId, rate: "", effectiveDate: "", confidence: "High", remarks: "" })}>new rate</Button>
                            <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => changeStatus(r, "Invalid")}>invalid</Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : <div className="small text-muted mt-2">No vendor has quoted this size yet.</div>}
              {v.history.length > v.current.length && (
                <div>
                  <Button size="sm" variant="link" className="p-0 small" onClick={() => setOpen({ ...open, [v.id]: !open[v.id] })}>{open[v.id] ? "Hide" : "Show"} full history ({v.history.length} rates)</Button>
                  {open[v.id] && (
                    <table className="table table-sm mb-0 mt-1 small">
                      <tbody>
                        {v.history.map((r) => (
                          <tr key={r.id}>
                            <td>{r.effectiveDate}</td><td>{r.vendorName}</td><td className="text-end">{inr(r.rate)}</td>
                            <td><span className={`badge bg-${STATUS_CLASS[r.status] || "light text-dark"}`}>{r.status || "no status"}</span></td>
                            <td className="text-muted">{r.source || ""}</td>
                            <td className="text-end">
                              {meta.rateStatuses.filter((s) => s !== r.status).map((s) => <Button key={s} size="sm" variant="link" className="p-0 ms-2" onClick={() => changeStatus(r, s)}>{s.toLowerCase()}</Button>)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {!p.variants.length && <div className="card"><div className="card-body text-muted">No variants yet — add one with a height and/or bag size.</div></div>}
      </div>

      <Modal show={!!edit} onHide={() => setEdit(null)}>
        <Modal.Header closeButton><Modal.Title>Edit product</Modal.Title></Modal.Header>
        {edit && <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>Name</Form.Label><Form.Control value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Botanical name</Form.Label><Form.Control value={edit.scientificName} onChange={(e) => setEdit({ ...edit, scientificName: e.target.value })} /></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Category</Form.Label><Form.Select value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })}><option value="">—</option>{meta.categories.map((c) => <option key={c}>{c}</option>)}</Form.Select></Form.Group>
          <Form.Group><Form.Label>Name source</Form.Label><Form.Select value={edit.nameSource} onChange={(e) => setEdit({ ...edit, nameSource: e.target.value })}><option value="">—</option>{meta.nameSources.map((c) => <option key={c}>{c}</option>)}</Form.Select></Form.Group>
        </Modal.Body>}
        <Modal.Footer><Button variant="success" disabled={busy} onClick={saveProduct}>Save to Zoho</Button></Modal.Footer>
      </Modal>

      <Modal show={!!variantModal} onHide={() => setVariantModal(null)}>
        <Modal.Header closeButton><Modal.Title>{variantModal?.id ? "Edit size" : "Add variant"}</Modal.Title></Modal.Header>
        {variantModal && <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>Height / size</Form.Label><Form.Control placeholder="4-5 ft, 12 inch, 1 kg…" value={variantModal.height} onChange={(e) => setVariantModal({ ...variantModal, height: e.target.value })} autoFocus /></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Bag / pot size</Form.Label><Form.Control placeholder="7x8, 11x11 in…" value={variantModal.bagSize} onChange={(e) => setVariantModal({ ...variantModal, bagSize: e.target.value })} /></Form.Group>
          <div className="small text-muted">Write the vendor's real value — the picklists are not restrictive. Name becomes <b>{p.name} ({[variantModal.height, variantModal.bagSize].filter(Boolean).join(", ") || "…"})</b>.</div>
        </Modal.Body>}
        <Modal.Footer><Button variant="success" disabled={busy || !(variantModal?.height || variantModal?.bagSize)} onClick={saveVariant}>Save to Zoho</Button></Modal.Footer>
      </Modal>

      <Modal show={!!rateModal} onHide={() => setRateModal(null)}>
        <Modal.Header closeButton><Modal.Title>Add rate — {rateModal?.variantName}</Modal.Title></Modal.Header>
        {rateModal && <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>Vendor</Form.Label>
            <Form.Select value={rateModal.vendorId} onChange={(e) => setRateModal({ ...rateModal, vendorId: e.target.value })} autoFocus>
              <option value="">— pick —</option>{realVendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </Form.Select>
          </Form.Group>
          <div className="row g-2">
            <div className="col-6"><Form.Group className="mb-2"><Form.Label>Rate (₹)</Form.Label><Form.Control type="number" step="0.01" min="0" value={rateModal.rate} onChange={(e) => setRateModal({ ...rateModal, rate: e.target.value })} /></Form.Group></div>
            <div className="col-6"><Form.Group className="mb-2"><Form.Label>Quoted on</Form.Label><Form.Control type="date" value={rateModal.effectiveDate} onChange={(e) => setRateModal({ ...rateModal, effectiveDate: e.target.value })} /></Form.Group></div>
          </div>
          <Form.Group className="mb-2"><Form.Label>Confidence</Form.Label><Form.Select value={rateModal.confidence} onChange={(e) => setRateModal({ ...rateModal, confidence: e.target.value })}><option>High</option><option>Medium</option><option>Low</option></Form.Select></Form.Group>
          <Form.Group><Form.Label>Remarks</Form.Label><Form.Control placeholder="excl. GST, per kg, what the vendor called it…" value={rateModal.remarks} onChange={(e) => setRateModal({ ...rateModal, remarks: e.target.value })} /></Form.Group>
          <div className="small text-muted mt-2">Always a new rate row; earlier rates stay as history.</div>
        </Modal.Body>}
        <Modal.Footer><Button variant="success" disabled={busy || !rateModal?.vendorId || !(Number(rateModal?.rate) > 0)} onClick={saveRate}>Log rate in Zoho</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductPage;
